/**
 * Module 122 — a layout readout for checking a real phone, shown ONLY when the
 * page URL carries `bjdebug=layout`. Without the flag nothing is created.
 *
 * It answers the one question a screenshot cannot: what heights the browser is
 * reporting versus the space the game really got. On the S25 Ultra in Samsung
 * Internet the first suspicion (`100dvh` ≈ the full screen) was disproved by
 * this readout; the extra lines evaluate each term of the bar's shift on the
 * device itself, and say whether the measured fit (`fitRow2`) took over.
 *
 * Diagnostic numbers and CSS names only — no player-facing words, so no `t()`.
 */
export function wantsLayoutReadout(search) {
    return new URLSearchParams(search).get('bjdebug') === 'layout';
}
/** Mounts the readout; returns its teardown. */
export function mountLayoutReadout(doc = document) {
    const win = doc.defaultView;
    if (!win)
        return () => { };
    const box = doc.createElement('pre');
    box.setAttribute('data-testid', 'bj-layout-readout');
    box.style.cssText =
        'position:fixed;left:4px;bottom:4px;z-index:99999;margin:0;padding:6px 8px;' +
            'font:11px/1.35 ui-monospace,monospace;color:#0f0;background:rgba(0,0,0,.8);' +
            'border-radius:6px;pointer-events:none;white-space:pre';
    const probe = doc.createElement('div');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:100dvh;visibility:hidden';
    // The safe-area insets, read as padding (env() has no script API).
    const inset = doc.createElement('div');
    inset.style.cssText =
        'position:fixed;top:0;left:0;width:1px;height:1px;visibility:hidden;' +
            'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
    // Each term of the portrait Moves bar's shift (blockjam.css), evaluated by
    // the browser itself as a `top` (which, unlike a height, keeps its sign).
    const T = '(var(--app-top-bar-h) + var(--bj-port-row2)) * var(--bj-s, 1)';
    const terms = [
        ['envTop', 'env(safe-area-inset-top, 0px)'],
        ['bar', 'var(--app-top-bar-h)'],
        ['row', 'var(--bj-port-row2)'],
        ['T', `calc(${T})`],
        ['F', 'var(--bj-port-board-top)'],
        ['A', `calc((var(--bj-port-board-top) - env(safe-area-inset-top, 0px) - ${T}) / 2)`],
        [
            'B',
            `calc((var(--bj-board-top-measured, 100000px) - env(safe-area-inset-top, 0px) - ${T}) / 2 + 0.5px)`,
        ],
    ];
    const termEls = terms.map(([, expr]) => {
        const el = doc.createElement('div');
        el.style.cssText = 'position:fixed;left:0;width:1px;height:1px;visibility:hidden';
        el.style.setProperty('top', expr);
        return el;
    });
    doc.body.append(box, probe, inset, ...termEls);
    const px = (v) => (v === undefined ? '—' : `${Math.round(v * 10) / 10}`);
    const rect = (sel) => doc.querySelector(sel)?.getBoundingClientRect();
    const cssVar = (name) => win.getComputedStyle(doc.documentElement).getPropertyValue(name).trim() || '—';
    const mq = (q) => typeof win.matchMedia === 'function' ? win.matchMedia(q).matches : '—';
    const update = () => {
        const container = rect('.game-container');
        const canvas = rect('.game-container canvas');
        const status = rect('[data-testid="bj-status"]');
        const range = (r) => (r ? `${px(r.top)}-${px(r.bottom)}` : '—');
        const row2 = doc.querySelector('.bj-hud .game-layout__row2');
        const insetCs = win.getComputedStyle(inset);
        box.textContent = [
            `inner ${win.innerWidth}x${win.innerHeight}  vv ${px(win.visualViewport?.height)}`,
            `100dvh ${px(probe.getBoundingClientRect().height)}  container ${px(container?.height)}`,
            `mq<=720 ${mq('(max-height: 720px)')}  <=760 ${mq('(max-height: 760px)')}`,
            `board top ${px(canvas?.top)}  measured ${cssVar('--bj-board-top-measured')}`,
            `moves bar ${px(status?.top)}-${px(status?.bottom)}  dpr ${win.devicePixelRatio}`,
            `topbar ${range(rect('.bj-hud .app-top-bar'))}  region ${range(rect('.bj-hud .game-layout__top-bar'))}`,
            `row2 ${range(row2?.getBoundingClientRect())}  inner ${range(rect('.bj-hud .bj-hud__row2'))}`,
            `row2 shift ${row2 ? win.getComputedStyle(row2).transform : '—'}`,
            `boardbox ${range(rect('.bj-hud .game-layout__board'))}  hud ${range(rect('.bj-hud-root'))}`,
            `safe ${insetCs.paddingTop}/${insetCs.paddingBottom}  rem ${win.getComputedStyle(doc.documentElement).fontSize}  scale ${cssVar('--ui-scale')}`,
            terms
                .slice(0, 4)
                .map(([name], i) => `${name} ${win.getComputedStyle(termEls[i]).top}`)
                .join('  '),
            terms
                .slice(4)
                .map(([name], i) => `${name} ${win.getComputedStyle(termEls[i + 4]).top}`)
                .join('  '),
            `fit ${row2?.getAttribute('data-bj-row2-fit') ? 'ON' : 'off'}  ${/(Chrome|SamsungBrowser)\/[\d.]+/.exec(win.navigator.userAgent)?.[0] ?? '—'}`,
        ].join('\n');
    };
    const timer = win.setInterval(update, 500);
    update();
    return () => {
        win.clearInterval(timer);
        box.remove();
        probe.remove();
        inset.remove();
        termEls.forEach((el) => el.remove());
    };
}
